import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import usePlanStore from 'store/planStore';
import { Touchpoints } from 'pages/calculator/MediaPlanning/Plan/enums';
import {
  CalculatedBudget,
  OfflineBudgetEntry,
} from '../../../../../../../../store/planStore';

export interface TotalBudgetSummaryProps {
  digital: (CalculatedBudget | undefined)[] | undefined;
  offline: OfflineBudgetEntry[] | undefined;
}

export function TotalBudgetSummary({
  digital,
  offline,
}: TotalBudgetSummaryProps) {
  const { t } = useTranslation();
  const defaultCurrency = 'PLN';

  const { countryForms, selectedCountry, savedCountryForms } = usePlanStore();
  const countryForm = selectedCountry ? countryForms[selectedCountry] : null;
  const savedForm = selectedCountry ? savedCountryForms[selectedCountry] : null;

  const tvAvailable = countryForm?.availableTouchpoints?.some(
    (tp) => tp.TouchpointName === Touchpoints.tv
  );
  const isTVMandatory = savedForm?.isTVMandatory ?? countryForm?.isTVMandatory;
  const isTVIncluded = savedForm?.isTVIncluded ?? countryForm?.isTVIncluded;

  const getTVLabel = (): string | null => {
    if (!tvAvailable) return 'TV: Not Included (not an option)';
    if (isTVMandatory) return 'TV: Included (mandatory)';
    if (isTVIncluded === true) return 'TV: Included (manually selected)';
    if (isTVIncluded === false) return 'TV: Not Included (manually selected)';
    return null;
  };
  const tvLabel = getTVLabel();

  const sumBudgets = () => {
    const summedDigital = digital?.reduce(
      (sum, touchpoint) => {
        return {
          value: sum.value + (touchpoint?.budget.value ?? 0),
          currency: touchpoint?.budget.currency || defaultCurrency,
        };
      },
      { value: 0, currency: defaultCurrency }
    );

    const summedOffline = offline
      ?.filter((touchpoint) => touchpoint.touchpoint !== 'tech cost')
      .reduce(
        (acc, touchpoint) => ({
          currency: touchpoint.budget.currency,
          value: acc.value + touchpoint.budget.value,
        }),
        { currency: defaultCurrency, value: 0 }
      );

    return {
      value: (summedDigital?.value ?? 0) + (summedOffline?.value ?? 0),
      currency: summedDigital?.currency ?? summedOffline?.currency,
    };
  };

  const sumTechCosts = () => {
    const digitalTechCosts =
      digital?.reduce((sum, row) => {
        const dsp = row?.techCost?.dsp || 0;
        const adServer = row?.techCost?.adServer || 0;
        return sum + dsp + adServer;
      }, 0) || 0;

    const offlineTechCosts =
      offline?.reduce((sum, row) => {
        return row.touchpoint === 'tech cost'
          ? sum + (row.budget?.value || 0)
          : sum;
      }, 0) || 0;

    return digitalTechCosts + offlineTechCosts;
  };

  return (
    <Grid
      container
      sx={{
        p: '24px 15px',
        backgroundColor: 'tableBackground.main',
      }}
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography textTransform="uppercase" fontSize={18} fontWeight={700}>
          {t('mediaPlanning.totalBudget')}
        </Typography>
        <Typography fontWeight={700} fontSize={18}>
          {`${formatNumberWithSpaces(sumBudgets().value.toFixed(0))} ${
            sumBudgets().currency
          }`}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography textTransform="uppercase" variant="body2" fontWeight={700}>
          {t('mediaPlanning.techCosts')}
        </Typography>
        <Typography fontWeight={700} variant="body2">
          {`${formatNumberWithSpaces(sumTechCosts().toFixed(0))} ${
            offline ? offline[0].budget.currency : defaultCurrency
          }`}
        </Typography>
      </Grid>
      {tvLabel && (
        <>
          <Grid item xs={12}>
            <Box sx={{ my: 1 }}>
              <Divider sx={{ borderStyle: 'dashed' }} />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography fontSize={12} fontWeight={700}>
              {tvLabel}
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  );
}
